/**
 * Teen Journey Completion Celebration
 * Full-screen celebration with confetti when teens complete all 21 days.
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Star, Sparkles, Share2, X, Rocket, Flame, Zap, RefreshCw, AlertCircle } from "lucide-react";
import Confetti from "react-confetti";

const JourneyCompleteCelebration = ({ 
  isOpen, 
  onClose, 
  stats = {},
  onShare,
  onRestart
}) => {
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [showConfetti, setShowConfetti] = useState(true);
  const [confettiPieces, setConfettiPieces] = useState(200);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    
    // Reduce confetti after initial burst
    const timer = setTimeout(() => {
      setConfettiPieces(50);
    }, 3000);
    
    // Stop confetti after 8 seconds
    const stopTimer = setTimeout(() => {
      setShowConfetti(false);
    }, 8000);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
      clearTimeout(stopTimer);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const totalXP = stats.total_xp || 3150;
  const streak = stats.streak || 21;
  const reflections = stats.reflections_count || 21;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-gradient-to-b from-[#1a0a2e] via-[#2d1b4e] to-[#1a0a2e] flex flex-col items-center justify-center overflow-hidden"
      >
        {/* Confetti */}
        {showConfetti && (
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            numberOfPieces={confettiPieces}
            recycle={confettiPieces > 50}
            colors={['#9333ea', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ffffff']}
            gravity={0.15}
          />
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white z-50"
        >
          <X size={24} />
        </button>

        {/* Floating particles background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              initial={{
                x: Math.random() * windowSize.width,
                y: windowSize.height + 20,
              }}
              animate={{
                y: -20,
                x: Math.random() * windowSize.width,
              }}
              transition={{
                duration: 5 + Math.random() * 5,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            />
          ))}
        </div>

        {/* Main content */}
        <div className="relative z-10 flex flex-col items-center px-6 text-center max-w-md">
          {/* Trophy with glow */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
            className="relative mb-6"
          >
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500 flex items-center justify-center shadow-2xl">
              <Trophy size={64} className="text-white drop-shadow-lg" />
            </div>
            
            {/* Glow rings */}
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.2, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 w-32 h-32 rounded-full bg-amber-400/30 blur-xl -z-10"
            />
            <motion.div
              animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.1, 0.3] }}
              transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
              className="absolute inset-0 w-32 h-32 rounded-full bg-yellow-400/20 blur-2xl -z-20"
            />

            {/* Orbiting stars */}
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="absolute"
                style={{ top: '50%', left: '50%' }}
                animate={{ rotate: 360 }}
                transition={{ duration: 4 + i, repeat: Infinity, ease: "linear" }}
              >
                <Star
                  size={16}
                  className="text-yellow-300"
                  style={{
                    transform: `translate(-50%, -50%) translateX(${70 + i * 15}px)`
                  }}
                  fill="currentColor"
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <h1 className="text-4xl font-bold text-white mb-2">
              Challenge Complete!
            </h1>
            <p className="text-xl text-purple-300 mb-2">
              21 Days of Growth
            </p>
          </motion.div>

          {/* Achievement badge */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.7 }}
            className="px-6 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold mb-8 shadow-lg shadow-purple-500/30"
          >
            <span className="flex items-center gap-2">
              <Sparkles size={18} />
              Confidence Champion
              <Sparkles size={18} />
            </span>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="grid grid-cols-3 gap-6 mb-8 w-full"
          >
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Zap size={20} className="text-amber-400" />
                <span className="text-3xl font-bold text-white">{totalXP}</span>
              </div>
              <span className="text-white/60 text-sm">Total XP</span>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Flame size={20} className="text-orange-400" />
                <span className="text-3xl font-bold text-white">{streak}</span>
              </div>
              <span className="text-white/60 text-sm">Day Streak</span>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Rocket size={20} className="text-purple-400" />
                <span className="text-3xl font-bold text-white">{reflections}</span>
              </div>
              <span className="text-white/60 text-sm">Reflections</span>
            </div>
          </motion.div>

          {/* Message */}
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="text-white/80 text-lg mb-8 leading-relaxed"
          >
            You showed up for yourself every single day. 
            That takes real commitment. You've built habits that will 
            serve you for life. This is just the beginning of your journey.
          </motion.p>

          {/* Action buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="flex flex-col gap-4 items-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onShare}
              className="px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold shadow-xl shadow-purple-500/30 flex items-center gap-2"
            >
              <Share2 size={20} />
              Share Achievement
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowRestartConfirm(true)}
              className="px-6 py-3 rounded-xl bg-white/10 text-white/80 font-semibold flex items-center gap-2 hover:bg-white/20 transition-colors"
            >
              <RefreshCw size={18} />
              Start New Journey
            </motion.button>
          </motion.div>

          {/* Quote */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="mt-8 text-white/50 text-sm italic"
          >
            "The only person you are destined to become is the person you decide to be."
          </motion.p>
        </div>

        {/* Restart Confirmation Modal */}
        <AnimatePresence>
          {showRestartConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 flex items-center justify-center z-50 px-6"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-[#1a1a3e] rounded-2xl p-6 max-w-sm w-full border border-white/10"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <RefreshCw size={24} className="text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Start New Journey?</h3>
                    <p className="text-white/60 text-sm">Your achievement is saved!</p>
                  </div>
                </div>

                <p className="text-white/70 text-sm mb-6">
                  Ready for another round? Your previous journey completion will be saved in your history. 
                  You'll start fresh with Day 1 and can earn all the XP again!
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRestartConfirm(false)}
                    className="flex-1 py-3 rounded-xl bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowRestartConfirm(false);
                      onRestart();
                    }}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold shadow-lg shadow-purple-500/30"
                  >
                    Let's Go!
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default JourneyCompleteCelebration;
